/*
 * Copyright Deskshare, Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * Refunds API Service
 *
 * S7 (Pass 8): Added eligibility endpoint call and free-text reason.
 */

import { createApi } from "@reduxjs/toolkit/query/react";
import {
  baseQueryWithReauth,
  CACHE_TIMES,
  TAG_TYPES,
} from "@/redux/api-config";

/**
 * S7 (Pass 8): Eligibility check request — server is the single authority.
 */
export interface CheckEligibilityRequest {
  payment_intent: string;
  /** B4 (FCR S497): optional — enables per-product refund limit check */
  product_id?: string;
}

/**
 * Eligibility response from the server (S5).
 */
export interface CheckEligibilityResponse {
  eligible: boolean;
  /** Machine-readable reason when not eligible */
  reason?: "window_expired" | "already_refunded" | "abuse_limit" | "no_payment_intent" | "superseded_by_upgrade" | "product_refund_limit_reached";
  days_remaining?: number;
  days_since?: number;
  /** B4 (FCR S497): set when reason is product_refund_limit_reached */
  product_id?: string;
}

/**
 * Request payload for creating a refund.
 * S7 (Pass 8): reason is now a free string (not a fixed union) so the
 * customer-chosen reason from the dropdown passes through unchanged.
 */
export interface CreateRefundRequest {
  /** Payment Intent ID to refund */
  payment_intent: string;
  /** Optional: Amount to refund in cents (if partial refund) */
  amount?: number;
  /**
   * S7 (Pass 8): Widened to string — the customer's chosen reason from the
   * dropdown. The server maps this to the appropriate Stripe reason enum.
   */
  reason?: string;
  /** Optional: Free-text note from the customer */
  reason_note?: string;
  /**
   * SK-I3 B (FCR S505): Stripe product ID of the specific line to refund on
   * a multi-product invoice. When present, the backend scopes the Stripe
   * refund amount to that product's line amount and filters license revocation
   * to only the matching QLM key. Omit for full-invoice refunds.
   */
  product_id?: string;
  /** Optional: Additional metadata */
  metadata?: Record<string, string>;
}

/**
 * Response from Stripe refund creation
 */
export interface RefundResponse {
  id: string;
  object: "refund";
  amount: number;
  balance_transaction: string | null;
  charge: string | null;
  created: number;
  currency: string;
  description: string | null;
  payment_intent: string | null;
  reason: string | null;
  receipt_number: string | null;
  status: "pending" | "succeeded" | "failed" | "canceled";
}

export interface RefundError {
  error: string;
  code?: string;
  details?: Record<string, unknown>;
}

export const refundsApi = createApi({
  reducerPath: "refundsApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: [TAG_TYPES.ORDERS, TAG_TYPES.INVOICES],
  endpoints: (builder) => ({
    /**
     * S5/S7 (Pass 8): Check refund eligibility from the server before showing
     * options to the customer. The server is the single authority on the
     * 14-day window, ownership, and the abuse limit (N-6).
     */
    checkEligibility: builder.mutation<
      CheckEligibilityResponse,
      CheckEligibilityRequest
    >({
      query: (body) => ({
        url: "refund/check-eligibility/",
        method: "POST",
        body,
      }),
      // Eligibility is read-only — no cache invalidation needed.
    }),

    /**
     * Create a new refund for a payment.
     */
    createRefund: builder.mutation<RefundResponse, CreateRefundRequest>({
      query: (body) => ({
        url: "refund/",
        method: "POST",
        body,
      }),
      // Invalidate orders cache after successful refund so the billing table refreshes.
      invalidatesTags: [
        { type: TAG_TYPES.ORDERS, id: "LIST" },
        { type: TAG_TYPES.INVOICES, id: "LIST" },
      ],
    }),

    /**
     * Get refund details by ID.
     */
    getRefund: builder.query<RefundResponse, string>({
      query: (refundId) => `refund/${refundId}/`,
      keepUnusedDataFor: CACHE_TIMES.LONG,
    }),
  }),
});

export const {
  useCheckEligibilityMutation,
  useCreateRefundMutation,
  useGetRefundQuery,
  useLazyGetRefundQuery,
} = refundsApi;
